import { List } from "immutable";
import { isNil } from "lodash";
import { createSelector } from "reselect";

import timelineUtils from "../../features/timelineView/utils";
import { createFromJS, Precision } from "../../types";
import { DateRecord } from "../../types/immutableRecords/model/data/DateRecord";
import { getLabelWithUntitled } from "../../utils/labelUtils";
import dateFunctions, { isDateValid } from "../definition/calendar/dateFunctions";
import dateSubtractionFunctions from "../definition/calendar/dateFunctions/dateSubtractionFunctions";
// import { MAX_CALENDAR_MARKER_REPEATS_PER_SCREEN } from "../../constants";
import {
  precisionForTimespan,
  timespanForComponents,
  timespanForPrecisionAndUnits,
  unitsForTimespan} from "../definition/calendar/dateFunctions/timespanFunctions";

const getCalendarMarkersById = state => state.data.calendarMarkers.byId;
const getCalendarMarkerIds = state => state.data.calendarMarkers.allIds;

const getCalendarMarkersList = createSelector(
  // Select the variables
  state => state.data.calendarMarkers,
  // Now do the calculation
  markers => markers.allIds.map(id => markers.byId.get(id))
);

// Iterate through calendar markers and find any that fall between
// the provided dates.
const getDisplayCalendarMarkersBetweenDates = (markers, calendar, startTs, endTs, minRepeatSeconds) => {
  // Calculate the date at the start of the display
  const displayStartDate = dateFunctions.dateWithTimestamp(calendar, startTs, Precision.Second);

  let visibleMarkers = [];

  markers.forEach(m => {
    let repeatFrequency = m.repeatFrequency;
    let repeatEveryXFrequency = 1;
    const avgRepeatSeconds = m.repeat ? dateFunctions.approxSecondsInTimespan(calendar, m.repeatFrequency) : null;

    if ((m.repeat && avgRepeatSeconds === 0) || !isDateValid(m.startDate)) {
      return;
    }
    if (m.repeat && avgRepeatSeconds < minRepeatSeconds) {
      const quotient = minRepeatSeconds / avgRepeatSeconds;
      let scale = Math.pow(10, Math.floor(Math.log10(quotient)));
      let multiplier = quotient / scale;
      if (multiplier > 5) {
        multiplier = 1;
        scale = scale * 10;
      } else if (multiplier > 2) {
        multiplier = 5;
      } else if (multiplier > 1) {
        multiplier = 2;
      } else {
        multiplier = 1;
      }

      repeatEveryXFrequency = multiplier * scale;
      repeatFrequency = timespanForComponents(
        m.repeatFrequency.years * repeatEveryXFrequency,
        m.repeatFrequency.months * repeatEveryXFrequency,
        m.repeatFrequency.weeks * repeatEveryXFrequency,
        m.repeatFrequency.days * repeatEveryXFrequency,
        m.repeatFrequency.hours * repeatEveryXFrequency,
        m.repeatFrequency.minutes * repeatEveryXFrequency,
        m.repeatFrequency.seconds * repeatEveryXFrequency
      );
    }

    const origStarDate = dateFunctions.dateWithTimestamp(calendar, m.startDate.timestamp, m.startDate.precision);
    let startDate = dateFunctions.dateWithTimestamp(calendar, m.startDate.timestamp, m.startDate.precision);
    startDate = startDate ? startDate : DateRecord();
    const stopTs = m.repeat
      ? m.repeatUntil.timestamp !== null
        ? m.repeatUntil.timestamp
        : endTs
      : startDate
        ? startDate.timestamp
        : null;

    // Now, let's work out how many repeats we can immediately jump ahead
    const repeatPrecision = precisionForTimespan(repeatFrequency);
    const repeatUnits = unitsForTimespan(repeatFrequency, repeatPrecision);
    let gapUnits =
      isNil(startDate) || isNil(startDate.timestamp)
        ? 0
        : dateSubtractionFunctions.unitsBetweenDates(calendar, startDate, displayStartDate, repeatPrecision);
    gapUnits = gapUnits - (gapUnits % repeatEveryXFrequency);
    // Round down to get to the item before the current start date...
    const addUnits = Math.floor(gapUnits / repeatUnits) * repeatUnits;
    if (addUnits > 0) {
      const addSpan = timespanForPrecisionAndUnits(repeatPrecision, addUnits);
      startDate = dateFunctions.dateByAddingSpan(calendar, startDate, addSpan);
    }
    let endDate = dateFunctions.dateByAddingSpan(calendar, startDate, m.duration);

    let occurrence = null;

    do {
      if (
        (startDate.timestamp >= startTs && startDate.timestamp <= endTs) ||
        (endDate.timestamp >= startTs && endDate.timestamp <= endTs) ||
        (startDate.timestamp <= startTs && endDate.timestamp >= endTs)
      ) {
        occurrence = !occurrence
          ? isNil(startDate) || isNil(startDate.timestamp)
            ? 0
            : dateSubtractionFunctions.unitsBetweenDates(calendar, origStarDate, startDate, repeatPrecision) + 1
          : occurrence + repeatEveryXFrequency;
        const label = `${getLabelWithUntitled(m.label)}: ${timelineUtils.ordinalString(occurrence)} occurrence`;

        visibleMarkers.push(createFromJS.CalendarMarkerDisplayRecord(m).merge({ startDate, endDate, label }));
      }
      startDate = dateFunctions.dateByAddingSpan(calendar, startDate, repeatFrequency);
      endDate = dateFunctions.dateByAddingSpan(calendar, startDate, m.duration);
    } while (m.repeat && startDate.timestamp <= stopTs);
  });

  return List(visibleMarkers);
};

// // May choose to switch this to list selector if it is slow enough, but suspect it is a fast function so avoiding
// // premature optimisation.
// const getDisplayCalendarMarkersBetweenDates = createSelector(
//   // inputs
//   (state, _startTs, _endTs) => state.data.calenadarMarkers.byId.toList(),
//   (state, _startTs, _endTs) => calendarSelectors.getCalendar(state),
//   (_state, startTs, _endTs) => startTs,
//   (_state, _startTs, endTs) => endTs,
//   // calculate based on inputs
//   getRepeatedMarkersBetweenTimestamps
// );

export default {
  getCalendarMarkersById,
  getCalendarMarkerIds,
  getCalendarMarkersList,
  getDisplayCalendarMarkersBetweenDates
};
